/**
 * プレゼントボックスとログイン報酬（2026-10-05）。
 * プレゼントボックス＝読者に届いた贈り物を、押して受け取るまで預かる箱。期限は無い。出どころは問わない
 * （いまはログイン報酬だけ。運営からの配布もここに入れる）。
 * ログイン報酬＝アプリを開いた日の累計で数える（続けて開かなくても減らない）。日の区切りは魔力の配布と同じ時刻。
 * 魔力は入れない（デイリーとログイン報酬に魔力を入れないのは本人の決定）。
 *
 * 表の正本は Firestore（gachaConfig/loginRewards・管理UIの「ゲームの仕組み › ログイン報酬」で編集）。
 * 管理UI（local-ui）も写しで読むので、import は書かないこと（書くなら相対パスだけ）。
 */

export const LOGIN_REWARD_CONFIG_PATH = "gachaConfig/loginRewards";

/** 贈り物1つ。素材・精霊・本（スキルツリーのノードの報酬と同じ中身）と、精霊の経験値・ランダムな素材 */
export type PresentReward =
  | { type: "material"; materialId: string; name: string; quantity: number; imageUrl?: string }
  | {
      type: "spirit";
      spiritId: string;
      name: string;
      genre: string;
      rarity: string;
      imageUrl?: string;
      maxEvolutions?: number;
      canCrossEvolve?: boolean;
      crossEvolveGenres?: string[];
    }
  | { type: "novel"; novelId: string; title?: string; cluster?: string }
  /** 手持ちの精霊それぞれに経験値（2026-10-06・デイリーミッションの「育てる」向け） */
  | { type: "spirit_exp"; amount: number }
  /** 素材の一覧からランダムに quantity 個（2026-10-06。素材を名指ししなくても渡せる） */
  | { type: "material_random"; quantity: number };

export type PresentRewardType = PresentReward["type"];

/** 累計 day 日目に渡すもの */
export interface LoginRewardDay {
  day: number;
  rewards: PresentReward[];
}

export interface LoginRewardConfig {
  /** 表に書いた日は、その日の中身だけを渡す */
  days: LoginRewardDay[];
  /** 表に無い日に渡すもの（空なら何も渡さない） */
  everyday: PresentReward[];
}

/** 保存が無いときは何も渡さない（中身は管理UIで決める） */
export const DEFAULT_LOGIN_REWARDS: LoginRewardConfig = { days: [], everyday: [] };

/**
 * 管理UIの「ログイン報酬」に、保存が無いうちに入れておく初期値（2026-10-12・Claude の案）。保存するまで読者アプリでは配らない。
 * 毎日は素材（ランダム）2個、節目の日（1・3・7・14・21・30日目）に多め。30日でおよそ素材106個・経験値200
 * （進化の1段目＝素材20個と、2段目＝60個の半分ほど。デイリーミッション1日分＝素材およそ9個より控えめ）
 */
export const INITIAL_LOGIN_REWARDS: LoginRewardConfig = {
  everyday: [{ type: "material_random", quantity: 2 }],
  days: [
    { day: 1, rewards: [{ type: "material_random", quantity: 5 }, { type: "spirit_exp", amount: 30 }] },
    { day: 3, rewards: [{ type: "material_random", quantity: 3 }, { type: "spirit_exp", amount: 20 }] },
    { day: 7, rewards: [{ type: "material_random", quantity: 10 }] },
    { day: 14, rewards: [{ type: "material_random", quantity: 10 }, { type: "spirit_exp", amount: 50 }] },
    { day: 21, rewards: [{ type: "material_random", quantity: 10 }] },
    { day: 30, rewards: [{ type: "material_random", quantity: 20 }, { type: "spirit_exp", amount: 100 }] },
  ],
};

const str = (v: unknown) => (typeof v === "string" ? v : "");

function parseReward(raw: unknown): PresentReward | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  // Firestore は undefined を書けないので、絵が無いときは欄ごと置かない
  const image = str(r.imageUrl) ? { imageUrl: str(r.imageUrl) } : {};
  switch (r.type) {
    case "material": {
      const quantity = Math.floor(Number(r.quantity));
      if (!str(r.materialId) || !Number.isFinite(quantity) || quantity < 1) return null;
      return { type: "material", materialId: str(r.materialId), name: str(r.name), quantity, ...image };
    }
    case "spirit":
      if (!str(r.spiritId)) return null;
      return {
        type: "spirit",
        spiritId: str(r.spiritId),
        name: str(r.name),
        genre: str(r.genre),
        rarity: str(r.rarity) || "N",
        ...image,
        maxEvolutions: Number.isFinite(Number(r.maxEvolutions)) ? Number(r.maxEvolutions) : 0,
        canCrossEvolve: r.canCrossEvolve === true,
        crossEvolveGenres: Array.isArray(r.crossEvolveGenres) ? r.crossEvolveGenres.filter((g): g is string => typeof g === "string") : [],
      };
    case "novel":
      if (!str(r.novelId)) return null;
      return { type: "novel", novelId: str(r.novelId), title: str(r.title), cluster: str(r.cluster) };
    case "spirit_exp": {
      const amount = Math.floor(Number(r.amount));
      return Number.isFinite(amount) && amount >= 1 ? { type: "spirit_exp", amount } : null;
    }
    case "material_random": {
      const quantity = Math.floor(Number(r.quantity));
      return Number.isFinite(quantity) && quantity >= 1 ? { type: "material_random", quantity } : null;
    }
    default:
      return null;
  }
}

export function parseRewards(raw: unknown): PresentReward[] {
  return Array.isArray(raw) ? raw.map(parseReward).filter((r): r is PresentReward => r !== null) : [];
}

/** 保存された値を読める形にそろえる（日の順に並べ、同じ日の行は1つにまとめ、中身の無い行は捨てる） */
export function parseLoginRewardConfig(raw: unknown): LoginRewardConfig {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const byDay = new Map<number, PresentReward[]>();
  for (const row of Array.isArray(r.days) ? r.days : []) {
    if (!row || typeof row !== "object") continue;
    const day = Math.floor(Number((row as LoginRewardDay).day));
    if (!Number.isFinite(day) || day < 1) continue;
    const rewards = parseRewards((row as LoginRewardDay).rewards);
    if (rewards.length === 0) continue;
    byDay.set(day, [...(byDay.get(day) ?? []), ...rewards]);
  }
  const days = [...byDay.entries()].sort((a, b) => a[0] - b[0]).map(([day, rewards]) => ({ day, rewards }));
  return { days, everyday: parseRewards(r.everyday) };
}

/** 累計 day 日目に渡すもの */
export function loginRewardsForDay(config: LoginRewardConfig, day: number): PresentReward[] {
  return config.days.find((row) => row.day === day)?.rewards ?? config.everyday;
}

/**
 * 報酬が入った先（2026-10-12）。「報酬獲得！」の知らせの下の行に出す（文は台帳の quests.rewardToast.dest.*）。
 * presentBox はまだ受け取っていない（プレゼントボックスに届いた）。ほかは受け取って入った先
 */
export type RewardDestination = "presentBox" | "materials" | "spirits" | "shelf" | "party" | "hand" | "adventure" | "backgrounds" | "skills";

/** 手に入れた報酬1つ（知らせとプレゼントの受け取りの結果に並べる）。ランダムな素材は、実際に入った素材ごとに1つ */
export interface GrantedItem {
  label: string;
  imageUrl: string;
  destination: RewardDestination;
}

/** 贈り物が入る先（受け取ったとき） */
export function destinationOfReward(r: PresentReward): RewardDestination {
  switch (r.type) {
    case "material":
    case "material_random": return "materials";
    case "spirit":          return "spirits";
    case "novel":           return "shelf";
    case "spirit_exp":      return "party";
  }
}

/** 贈り物を一言で（「火の欠片×3」「精霊「ルミ」」「『灯台郵便局』」） */
export function presentRewardLabel(r: PresentReward): string {
  switch (r.type) {
    case "material": return `${r.name || "素材"}×${r.quantity}`;
    case "spirit":   return `精霊「${r.name || "？"}」`;
    case "novel":    return r.title ? `『${r.title}』` : "本1冊";
    case "spirit_exp":      return `手持ちの精霊に経験値+${r.amount}`;
    // 「（ランダム）」は付けない（10/12 本人「報酬の（ランダム）の記載いらない」）
    case "material_random": return `素材×${r.quantity}`;
  }
}

/** 一言を名前と数に分ける（「火の欠片×3」→ 火の欠片・×3）。封筒と貸出カードの窓で、数だけ太く右に寄せる */
export function splitRewardLabel(label: string): { name: string; amount: string } {
  const m = label.match(/^(.*?)\s*([×+]\d+)$/);
  return m ? { name: m[1], amount: m[2] } : { name: label, amount: "" };
}
